// react core
import { useContext, useState } from "react";

// date handling
import dayjs from "dayjs";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocketLaunch as iconLaunch } from "@fortawesome/pro-duotone-svg-icons";
import { faQuoteLeft as iconQuoteLeft } from "@fortawesome/pro-duotone-svg-icons";
import { faQuoteRight as iconQuoteRight } from "@fortawesome/pro-duotone-svg-icons";
import { faInfoCircle as iconWarning } from "@fortawesome/pro-duotone-svg-icons";

// entzy resources
import configEntzy from "components/config/ConfigEntzy";
import InputForm from "components/input/InputForm";
import { ActionButton } from "components/utils/common/CommonButtons";
import { BasicSelect } from "components/input/InputSelectors";

// entzy context and services
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import {
  createEventDateConstants,
  createEventSettingDateItem,
} from "models/event";

function ActionDatesRunner(props) {
  const user = props.user;

  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const dateConstants = createEventDateConstants();

  const actionParams = props.actionParams || {};
  const actionDateKey = actionParams.dateKey || null;

  const launchOverride = actionParams.launchOverride || false;
  const launchOverrideDate = {
    key: actionDateKey,
    display: actionDateKey ? dayjs(actionDateKey).format("DD MMM YYYY") : null,
  };
  const launchOverrideMessage = `Calendar Update: Run date ${launchOverrideDate.display} has been pre-launched and is now available for instant matches as a guaranteed date.`;
  const launchOverrideActivated =
    launchOverride &&
    launchOverrideDate.key &&
    eventContext.state.event.calendar.dates.runners.overrides.find(
      (obj) => obj.id === launchOverrideDate.key,
    ) !== undefined;

  const anchorTop = "anchor-action-dates-runner";

  const [launchOfferType, setLaunchOfferType] = useState(
    eventContext.state.event.calendar.tickets.selectable.active[0] || null,
  );
  const [launchIncludeMessage, setLaunchIncludeMessage] = useState(false);

  const handleActionCallback = async (data) => {
    const dateValueList = data.filter((obj) => obj.id === "event-dates")[0]
      .value;

    // replace runner date settings with new list
    // retain any existing act overrides from current settings
    const datesRunners = createEventSettingDateItem({ type: "default" });
    for (let item of dateValueList) {
      datesRunners.add.push(item);
    }
    const runnersAct =
      eventContext.state.event.calendar.dates.runners.settings.act || [];
    datesRunners.act = [...runnersAct];

    // pass through rider date settings unaltered
    const datesRiders = {
      ...eventContext.state.event.calendar.dates.riders.settings,
    };

    // rebuild event dates and post update
    const eventDates = [];
    eventDates.push(datesRunners);
    eventDates.push(datesRiders);
    if (eventContext.state.event.manager) {
      const eventUpdate = await eventContext.prepareUpdateEvent({
        key: "Dates",
        value: eventDates,
      });
      if (eventUpdate.alert) {
        return {
          alert: true,
          message: eventUpdate.message,
        };
      } else {
        eventContext.updateEvent(eventUpdate);
        props.handleCloseAction();
        mainContext.updateViewerTrail({
          eventDateSelect: null,
        });
        return { alert: false };
      }
    } else {
      return {
        alert: true,
        message: "Runner permissions required for this update",
      };
    }
  };

  const handleLaunchOverride = async () => {
    if (!eventContext.state.event.manager) {
      mainContext.updateAlert({
        show: true,
        message: "Runner permissions required for this update",
      });
      return;
    }

    // calculate integer date offset from base for the override date
    const baseDate = dayjs(dateConstants.base);
    const overrideDate = dayjs(launchOverrideDate.key);
    const dateOffset = overrideDate.diff(baseDate, "day");

    const datesRunners = createEventSettingDateItem({ type: "default" });
    const datesRiders = {
      ...eventContext.state.event.calendar.dates.riders.settings,
    };

    // add the integer offset to the runner date settings add and act lists
    const runnersAdd =
      eventContext.state.event.calendar.dates.runners.settings.add || [];
    const runnersAct =
      eventContext.state.event.calendar.dates.runners.settings.act || [];
    datesRunners.add = [...runnersAdd];
    datesRunners.act = [...runnersAct];
    if (!datesRunners.add.includes(dateOffset)) {
      datesRunners.add.push(dateOffset);
    }
    if (!datesRunners.act.includes(dateOffset)) {
      datesRunners.act.push(dateOffset);
    }

    // capture offer type if launch per ticket enabled
    // send the id as ephemeral value in the setting type
    // picked up in backend based on prefix typeid:offset
    if (eventContext.state.event.data.LaunchPerTicket) {
      if (!launchOfferType) {
        mainContext.updateAlert({
          show: true,
          message: "Please select an offer type to trigger the launch",
        });
        return;
      }
      datesRunners.type = `typeid:${dateOffset}:${launchOfferType.value}`;
    }

    // rebuild event dates and post update
    const eventDates = [];
    eventDates.push(datesRunners);
    eventDates.push(datesRiders);

    // initiate main loader
    mainContext.setCallbackLoader(true);

    // execute the event dates update
    const eventUpdate = await eventContext.prepareUpdateEvent({
      key: "Dates",
      value: eventDates,
    });
    if (eventUpdate.alert) {
      mainContext.setCallbackLoader(false);
      mainContext.updateAlert({
        show: true,
        message: eventUpdate.message,
      });
      return;
    }
    eventContext.updateEvent(eventUpdate);

    // post override launch message in main chat
    if (launchIncludeMessage) {
      const messagePayload = await eventContext.preparePostMessage({
        EventId: eventContext.state.event.data.EventId,
        RoomName: "main",
        ContentType: "message",
        ContentData: launchOverrideMessage,
      });
      if (messagePayload.alert) {
        mainContext.setCallbackLoader(false);
        mainContext.updateAlert({
          show: true,
          message: messagePayload.message,
        });
        return;
      }
      eventContext.postMessage(messagePayload.data);
    }

    // close action and main loader
    props.handleCloseAction();
    mainContext.updateViewerTrail({
      eventDateSelect: null,
    });

    // optionally close callback loader after timeout
    // however it should be closed with the observable updated
    // setTimeout(() => {
    //   mainContext.setCallbackLoader(false);
    // }, 3000);
  };

  return (
    <Box id={anchorTop} className="box-default">
      {launchOverride ? (
        <Box className="box-default" sx={{ py: configEntzy.APP_SPACING_MD }}>
          {launchOverrideActivated ? (
            <Box
              className="box-default shadow-dark"
              sx={{
                mt: configEntzy.APP_SPACING_MD2X,
                p: configEntzy.APP_SPACING_MD2X,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <Box className="box-default">
                <Typography variant="h1" color="primary">
                  <FontAwesomeIcon icon={iconLaunch} />
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ mt: configEntzy.APP_SPACING_MD }}
                >
                  Launched! {launchOverrideDate.display}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              className="box-default shadow-dark"
              sx={{
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <Box
                className="box-default"
                sx={{ p: configEntzy.APP_SPACING_MD2X }}
              >
                <Typography variant="h1" color="primary">
                  <FontAwesomeIcon icon={iconLaunch} />
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ mt: configEntzy.APP_SPACING_MD }}
                >
                  Launch Override: {launchOverrideDate.display}
                </Typography>
              </Box>
              <Box
                className="box-default"
                sx={{ px: configEntzy.APP_SPACING_MD2X }}
              >
                <Typography
                  variant="subtitle2"
                  className="text-paragraph"
                  color="white"
                >
                  This action will immediately launch your eventuator for the
                  selected date, overriding any minimum criteria.
                </Typography>
                <Typography
                  variant="subtitle2"
                  className="text-paragraph"
                  color="white"
                  sx={{ mt: configEntzy.APP_SPACING_MD }}
                >
                  Any offers in play will be immediately matched and notified
                  (ensure your riders will be happy with the new early trigger
                  conditions). Any future riders will be able to match
                  instantly, up to capacity.
                </Typography>
              </Box>
              <Container maxWidth="xs">
                <Box
                  className="box-default bg-black-t50"
                  sx={{
                    mt: configEntzy.APP_SPACING_MD2X,
                    pt: configEntzy.APP_SPACING_MD,
                    px: configEntzy.APP_SPACING_MD2X,
                    pb: configEntzy.APP_SPACING_MD,
                    borderRadius: configEntzy.BORDER_SIZE_XL,
                  }}
                >
                  <Typography
                    variant="h3"
                    className="text-paragraph"
                    color="primary"
                  >
                    <FontAwesomeIcon icon={iconWarning} />
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="text-paragraph"
                    color="primary"
                  >
                    Run dates cannot be unlaunched after trigger. Confirm below
                    to continue.
                  </Typography>
                </Box>
                {eventContext.state.event.data.LaunchPerTicket && (
                  <Box
                    className="box-default bg-black-t50"
                    sx={{
                      mt: configEntzy.APP_SPACING_MD,
                      px: configEntzy.APP_SPACING_MD2X,
                      pb: configEntzy.APP_SPACING_MD2X,
                      borderRadius: configEntzy.BORDER_SIZE_XL,
                    }}
                  >
                    <Box
                      className="box-default"
                      sx={{
                        pt: configEntzy.APP_SPACING_MD2X,
                        pb: configEntzy.APP_SPACING_MD,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        className="text-paragraph"
                        color="white"
                      >
                        Launch per offer type is currently enabled. Select the
                        offer type you wish to use to trigger the date.
                      </Typography>
                    </Box>
                    <Box className="box-default">
                      <BasicSelect
                        id="launch-offer-type-select"
                        label="Select Offer Type for Launch Override"
                        placeholder="Select offer type"
                        value={launchOfferType}
                        options={
                          eventContext.state.event.calendar.tickets.selectable
                            .active
                        }
                        onChange={(e, value) => setLaunchOfferType(value)}
                      />
                    </Box>
                  </Box>
                )}

                <Box
                  className="box-default"
                  sx={{
                    pt: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <ActionButton
                    variant="contained"
                    color="primary"
                    text={"Confirm Launch Date " + launchOverrideDate.display}
                    onClick={handleLaunchOverride}
                    fullWidth
                  />
                </Box>
                <Box
                  className="box-default"
                  sx={{
                    p: configEntzy.APP_SPACING_MD2X,
                  }}
                >
                  <Box className="box-default">
                    <Switch
                      checked={launchIncludeMessage}
                      onChange={(event) =>
                        setLaunchIncludeMessage(event.target.checked)
                      }
                      slotProps={{ input: { "aria-label": "controlled" } }}
                    />
                  </Box>
                  <Box className="box-default">
                    <Typography
                      variant="subtitle2"
                      color={launchIncludeMessage ? "primary" : "gray"}
                      className="text-paragraph"
                    >
                      {launchIncludeMessage
                        ? "The message below will be posted in main chat to announce this launch"
                        : "Include an automated message in main chat to announce this launch"}
                    </Typography>
                  </Box>
                </Box>
              </Container>
              <Box
                className="box-default bg-black-t25"
                sx={{
                  p: configEntzy.APP_SPACING_MD2X,
                  borderTop: "1px solid orange",
                  borderBottomLeftRadius: configEntzy.BORDER_SIZE_XL,
                  borderBottomRightRadius: configEntzy.BORDER_SIZE_XL,
                }}
                hidden={!launchIncludeMessage}
              >
                <Box
                  className="box-default"
                  sx={{
                    px: configEntzy.APP_SPACING_MD2X,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="white"
                    className="text-left"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    <FontAwesomeIcon icon={iconQuoteLeft} size="3x" />
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="white"
                    className="text-paragraph"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    {launchOverrideMessage}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="white"
                    className="text-right"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    <FontAwesomeIcon icon={iconQuoteRight} size="3x" />
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          className="box-default"
          sx={{
            mt: configEntzy.APP_SPACING_MD2X,
            pt: configEntzy.APP_SPACING_MD,
            borderTop: "1px solid #ccc",
            color: "white",
          }}
        >
          <InputForm
            navigate={props.navigate}
            fields={[
              {
                id: "event-dates",
                type: "picker-calendar",
                label: "Place your Run Date offers",
                placeholder: "Select one or many possible dates",
                value: eventContext.state.event.calendar.dates.selectable
                  .filter((obj) => obj.selected.runners === true)
                  .reduce((acc, obj) => [...acc, obj.value], []),
                required: true,
                disabled: !user.connected,
                options: eventContext.state.event.calendar.dates.selectable,
                optionsDecorate: {
                  ticking:
                    eventContext.state.event.calendar.dates.combined.active,
                  launched:
                    eventContext.state.event.calendar.dates.launched.all.keys,
                },
                optionsDisabled:
                  eventContext.state.event.calendar.dates.launched.all.keys,
              },
            ]}
            submitText="Update Date Offers"
            submitFixed={true}
            // disabledSubmit={!user.connected}
            // disabledTitle="Connect to continue"
            // disabledText="Tap here to activate date offering. A connection is required to save the offer details."
            // disabledClick={context.toggleConnect}
            callback={handleActionCallback}
          />
        </Box>
      )}
    </Box>
  );
}

export default ActionDatesRunner;
